import React, { useState,useEffect } from 'react';

import { 
  UserIcon, 
  BuildingIcon, 
  PhoneIcon, 
  MailIcon, 
  LockIcon, 
  EyeIcon, 
  EyeOffIcon, 
  SaveIcon, 
  ArrowLeftIcon 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../axios'; // adjust path to your API file

const UserSettingsForm = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [passwordVisible, setPasswordVisible] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const [formData, setFormData] = useState({
    email: '',
    companyName: '',
    phoneNumber: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await api.get('/user-details'); // replace with your endpoint
        const user = response.data;
  
        setFormData(prev => ({
          ...prev,
          email: user.email || '',
          companyName: user.company || '',
          phoneNumber: user.phone || '',
        }));
      } catch (err) {
        console.error('Failed to fetch user data:', err);
      }
    };
  
    fetchUser();
  }, []);

  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 4000); // Auto-hide after 4 seconds
  
      return () => clearTimeout(timer);
    }
  }, [error, success]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const togglePasswordVisibility = (field) => {
    setPasswordVisible(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
  
    try {
      if (activeTab === 'profile') {
        await api.put('/user/profile', {
          email: formData.email,
          company_name: formData.companyName,
          phone: formData.phoneNumber,
        });
        setSuccess('Profile updated successfully!');
      }
  
      if (activeTab === 'security') {
        await api.put('/user/password', {
          current_password: formData.currentPassword,
          new_password: formData.newPassword,
          new_password_confirmation: formData.confirmPassword,
        });
        setSuccess('Password updated successfully!');
      }
    } catch (err) {
      if (err.response?.data?.errors) {
        // Laravel validation errors
        const firstError = Object.values(err.response.data.errors)[0][0];
        setError(firstError);
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  
  



 

  return (
    <div className="w-full max-w-4xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden mt-20 mb-20">
      <div className="flex flex-col md:flex-row">
        {/* Sidebar */}
        <div className="w-full md:w-64 bg-gradient-to-b bg-blue-500 text-white p-6">
          <div className="mb-10">
            <h2 className="text-2xl font-bold">Settings</h2>
            <p className="text-indigo-200 mt-1 text-sm">Manage your account</p>
          </div>
          
          <nav className="space-y-1">
            <button 
              onClick={() => setActiveTab('profile')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                activeTab === 'profile' 
                  ? 'bg-white/10 text-white font-medium' 
                  : 'text-indigo-100 hover:bg-white/5'
              }`}
            >
              <UserIcon size={18} />
              <span>Profile Information</span>
            </button>
            
            <button 
              onClick={() => setActiveTab('security')}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                activeTab === 'security' 
                  ? 'bg-white/10 text-white font-mediu' 
                  : 'text-indigo-100 hover:bg-white/5'
              }`}
            >
              <LockIcon size={18} />
              <span>Password & Security</span>
            </button>
          </nav>
          
          <div className="absolute bottom-6 left-6 md:static md:mt-auto md:pt-6">
            <button 
              onClick={() =>{
                navigate('/customer');
              } 

              }
            className="text-indigo-200 hover:text-white flex items-center space-x-2 group transition-all">
              <ArrowLeftIcon size={16} className="group-hover:-translate-x-1 transition-transform" />
              <span>Back to Dashboard</span>
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8">
          {activeTab === 'profile' && (
            <div>
              <h3 className="text-2xl font-bold text-gray-800">Profile Information</h3>
              <p className="text-gray-500 mb-8">Update your personal information</p>
              {error && (
                <div className="bg-red-100 text-red-700 px-4 py-3 rounded mb-4 border border-red-200">
                  {error}
                </div>
              )}

              {success && (
                <div className="bg-green-100 text-green-700 px-4 py-3 rounded mb-4 border border-green-200">
                  {success}
                </div>
              )}

              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-1">
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                    <MailIcon size={16} className="mr-2 text-indigo-600" />
                    Email Address
                  </label>
                  <div className="relative">
                    <input 
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none"
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                    <BuildingIcon size={16} className="mr-2 text-indigo-600" />
                    Company Name
                  </label>
                  <div className="relative">
                    <input 
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none"
                      placeholder="Enter company name"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                    <PhoneIcon size={16} className="mr-2 text-indigo-600" />
                    Phone Number
                  </label>
                  <div className="relative">
                    <input 
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none"
                      placeholder="Enter phone number"
                    />
                  </div>
                </div>

                <div className="pt-4">
                  <button 
                    type="submit"
                    className="w-full md:w-auto flex items-center justify-center space-x-2 px-8 py-4 bg-blue-500 text-white font-medium rounded-xl hover:from-indigo-700 hover:to-purple-700 focus:ring-4 focus:ring-indigo-300 transition-all"
                  >
                    <SaveIcon size={18} />
                    {loading ? (
                      <span className="flex items-center">
                        <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="white" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="white" d="M4 12a8 8 0 018-8v8H4z"></path>
                        </svg>
                        Processing...
                      </span>
                    ) : (
                      <span>Save Changes</span>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {activeTab === 'security' && (
            <div>
              <h3 className="text-2xl font-bold text-gray-800">Password & Security</h3>
              <p className="text-gray-500 mb-8">Update your password</p>
               
                {error && (
                  <div className="bg-red-100 text-red-700 px-4 py-3 rounded mb-4 border border-red-200">
                    {error}
                  </div>
                )}

                {success && (
                  <div className="bg-green-100 text-green-700 px-4 py-3 rounded mb-4 border border-green-200">
                    {success}
                  </div>
                )}

              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700 mb-1">Current Password</label>
                  <div className="relative">
                    <input 
                      type={passwordVisible.current ? "text" : "password"}
                      name="currentPassword"
                      value={formData.currentPassword}
                      onChange={handleChange}
                      className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none pr-12"
                      placeholder="Enter current password"
                      required
                    />
                    <button 
                      type="button"
                      onClick={() => togglePasswordVisibility('current')}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {passwordVisible.current ? (
                        <EyeOffIcon size={20} />
                      ) : (
                        <EyeIcon size={20} />
                      )}
                    </button>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700 mb-1">New Password</label>
                  <div className="relative">
                    <input 
                      type={passwordVisible.new ? "text" : "password"}
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleChange}
                      className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none pr-12"
                      placeholder="Enter new password"
                      required
                    />
                    <button 
                      type="button"
                      onClick={() => togglePasswordVisibility('new')}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {passwordVisible.new ? (
                        <EyeOffIcon size={20} />
                      ) : (
                        <EyeIcon size={20} />
                      )}
                    </button>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                  <div className="relative">
                    <input 
                      type={passwordVisible.confirm ? "text" : "password"}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none pr-12"
                      placeholder="Confirm new password"
                      required
                    />
                    <button 
                      type="button"
                      onClick={() => togglePasswordVisibility('confirm')}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {passwordVisible.confirm ? (
                        <EyeOffIcon size={20} />
                      ) : (
                        <EyeIcon size={20} />
                      )}
                    </button>
                  </div>
                </div>

                <div className="pt-4">
                  <button 
                    type="submit"
                    className="w-full md:w-auto flex items-center justify-center space-x-2 px-8 py-4 bg-blue-500 text-white font-medium rounded-xl hover:from-indigo-700 hover:to-purple-700 focus:ring-4 focus:ring-indigo-300 transition-all"
                  >
                    <SaveIcon size={18} />
                    {loading ? (
                        <span className="flex items-center">
                          <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="white" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="white" d="M4 12a8 8 0 018-8v8H4z"></path>
                          </svg>
                          Processing...
                        </span>
                      ) : (
                        <span>Save Changes</span>
                      )}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserSettingsForm;